import React, { useCallback, useEffect, useState } from 'react';
import {
  AdminReviewApiError,
  fetchMotionReviewNotesInsertionRecords,
  type MotionReviewNotesInsertionRecord,
  type MotionReviewNotesInsertionRecordsResponse,
} from '../../lib/adminReviewApi';

export interface MotionReviewNotesInsertionLedgerPanelViewProps {
  uploadId: string;
  payload: MotionReviewNotesInsertionRecordsResponse | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const AdminOnlyBadge: React.FC = () => (
  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
    Admin only
  </span>
);

const AuditLedgerBadge: React.FC = () => (
  <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-200">
    Audit ledger
  </span>
);

const CustomerFlagOffBadge: React.FC = () => (
  <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-900 dark:bg-red-900/40 dark:text-red-200">
    Customer flag off
  </span>
);

export function insertionLedgerStatusLabel(status?: string): string {
  if (status === 'customer_report_insertion_candidate') return 'Insertion candidate';
  if (status === 'customer_report_inserted') return 'Inserted to Motion Review Notes web';
  if (status === 'customer_report_revoked') return 'Revoked from Motion Review Notes web';
  if (status === 'customer_report_rejected') return 'Rejected';
  if (status === 'customer_report_needs_review') return 'Needs review';
  return 'Unknown status';
}

function statusBadgeClass(status?: string): string {
  if (status === 'customer_report_inserted') {
    return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200';
  }
  if (status === 'customer_report_revoked') {
    return 'bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-200';
  }
  if (status === 'customer_report_rejected') {
    return 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200';
  }
  if (status === 'customer_report_needs_review') {
    return 'bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-200';
  }
  if (status === 'customer_report_insertion_candidate') {
    return 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200';
  }
  return 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-200';
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function renderFlag(label: string, value: boolean, testId: string): React.ReactNode {
  return (
    <span
      className="rounded bg-secondary-100 px-1.5 py-0.5 font-mono text-[10px] text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200"
      data-testid={testId}
    >
      {label}={String(value)}
    </span>
  );
}

function renderRecord(record: MotionReviewNotesInsertionRecord): React.ReactNode {
  return (
    <article
      key={record.window_id}
      className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-700"
      data-testid={`insertion-ledger-record-${record.window_id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-secondary-500 dark:text-secondary-400">
          {record.window_id}
        </span>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusBadgeClass(record.insertion_status)}`}
          data-testid={`insertion-ledger-status-${record.window_id}`}
        >
          {insertionLedgerStatusLabel(record.insertion_status)}
        </span>
      </div>
      <p
        className="mt-2 text-sm text-secondary-900 dark:text-white"
        data-testid={`insertion-ledger-statement-${record.window_id}`}
      >
        {record.statement}
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-secondary-600 dark:text-secondary-300">
        <div>
          <dt className="font-medium text-secondary-500 dark:text-secondary-400">Concept</dt>
          <dd data-testid={`insertion-ledger-concept-${record.window_id}`}>{record.concept_display}</dd>
        </div>
        <div>
          <dt className="font-medium text-secondary-500 dark:text-secondary-400">Attack area</dt>
          <dd data-testid={`insertion-ledger-attack-area-${record.window_id}`}>
            {record.attack_area_display}
          </dd>
        </div>
      </dl>
      <div className="mt-3 flex flex-wrap gap-2">
        {renderFlag(
          'visible_in_motion_review_notes',
          record.visible_in_motion_review_notes,
          `insertion-ledger-visible-${record.window_id}`
        )}
        {renderFlag(
          'published_to_report',
          record.published_to_report,
          `insertion-ledger-published-${record.window_id}`
        )}
        {renderFlag('customer_visible', record.customer_visible, `insertion-ledger-customer-visible-${record.window_id}`)}
        {renderFlag(
          'published_to_evidence_reel',
          record.published_to_evidence_reel,
          `insertion-ledger-evidence-reel-${record.window_id}`
        )}
        {renderFlag('delivered', record.delivered, `insertion-ledger-delivered-${record.window_id}`)}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-secondary-600 dark:text-secondary-300">
        <div>
          <dt className="font-medium text-secondary-500 dark:text-secondary-400">Inserted by</dt>
          <dd data-testid={`insertion-ledger-inserted-by-${record.window_id}`}>
            {record.inserted_by || '—'}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-secondary-500 dark:text-secondary-400">Inserted at</dt>
          <dd data-testid={`insertion-ledger-inserted-at-${record.window_id}`}>
            {formatTimestamp(record.inserted_at)}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-secondary-500 dark:text-secondary-400">
            Report inserted by
          </dt>
          <dd data-testid={`insertion-ledger-report-inserted-by-${record.window_id}`}>
            {record.customer_report_inserted_by || '—'}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-secondary-500 dark:text-secondary-400">
            Report inserted at
          </dt>
          <dd data-testid={`insertion-ledger-report-inserted-at-${record.window_id}`}>
            {formatTimestamp(record.customer_report_inserted_at)}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-secondary-500 dark:text-secondary-400">Revoked at</dt>
          <dd data-testid={`insertion-ledger-revoked-at-${record.window_id}`}>
            {formatTimestamp(record.revoked_at)}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-secondary-500 dark:text-secondary-400">Revoke reason</dt>
          <dd data-testid={`insertion-ledger-revoke-reason-${record.window_id}`}>
            {record.revoke_reason || '—'}
          </dd>
        </div>
      </dl>
    </article>
  );
}

export const MotionReviewNotesInsertionLedgerPanelView: React.FC<
  MotionReviewNotesInsertionLedgerPanelViewProps
> = ({ uploadId, payload, loading, error, onRefresh }) => {
  if (loading) {
    return (
      <section className="rounded-lg border border-secondary-200 p-4 text-sm text-secondary-600 dark:border-secondary-700 dark:text-secondary-300">
        Loading Motion Review Notes insertion ledger…
      </section>
    );
  }

  if (error) {
    return (
      <section
        className="rounded-lg border border-secondary-200 p-4 text-sm text-secondary-600 dark:border-secondary-700 dark:text-secondary-300"
        data-testid="insertion-ledger-error"
      >
        {error}
      </section>
    );
  }

  if (!payload) {
    return null;
  }

  const summary = payload.summary;
  const summaryCards = [
    { key: 'total', label: 'Total', value: summary.total_count, testId: 'insertion-ledger-total' },
    {
      key: 'candidate',
      label: 'Candidates',
      value: summary.candidate_count,
      testId: 'insertion-ledger-candidates',
    },
    {
      key: 'inserted',
      label: 'Inserted',
      value: summary.inserted_count,
      testId: 'insertion-ledger-inserted',
    },
    {
      key: 'revoked',
      label: 'Revoked',
      value: summary.revoked_count,
      testId: 'insertion-ledger-revoked',
    },
    {
      key: 'rejected',
      label: 'Rejected',
      value: summary.rejected_count,
      testId: 'insertion-ledger-rejected',
    },
    {
      key: 'needs_review',
      label: 'Needs review',
      value: summary.needs_review_count,
      testId: 'insertion-ledger-needs-review',
    },
    {
      key: 'unknown',
      label: 'Unknown',
      value: summary.unknown_count,
      testId: 'insertion-ledger-unknown',
    },
  ];

  return (
    <section
      className="space-y-4 rounded-lg border border-secondary-200 p-4 dark:border-secondary-700"
      data-testid="insertion-ledger-panel"
      data-upload-id={uploadId}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
          Motion Review Notes Insertion Ledger
        </h3>
        <AdminOnlyBadge />
        <AuditLedgerBadge />
        <CustomerFlagOffBadge />
      </div>

      <p
        className="text-xs text-amber-900 dark:text-amber-200"
        data-testid="insertion-ledger-warning"
      >
        This ledger is admin-only. It shows report-web insertion history and does not publish to
        the customer report, /full, Evidence Reel, delivery, exports, or commissioner surfaces.
      </p>

      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 lg:grid-cols-7">
        {summaryCards.map((card) => (
          <div
            key={card.key}
            className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60"
            data-testid={card.testId}
          >
            <div className="font-medium text-secondary-500 dark:text-secondary-400">{card.label}</div>
            <div className="text-lg font-semibold text-secondary-900 dark:text-white">{card.value}</div>
          </div>
        ))}
      </div>

      <div>
        <button
          type="button"
          className="btn-secondary text-xs"
          data-testid="insertion-ledger-refresh"
          onClick={onRefresh}
        >
          Refresh insertion ledger
        </button>
      </div>

      {payload.records.length > 0 ? (
        <div className="space-y-2">{payload.records.map(renderRecord)}</div>
      ) : (
        <p
          className="text-sm text-secondary-600 dark:text-secondary-300"
          data-testid="insertion-ledger-empty-state"
        >
          No Motion Review Notes insertion records exist for this report.
        </p>
      )}
    </section>
  );
};

interface MotionReviewNotesInsertionLedgerPanelProps {
  uploadId: string | null | undefined;
}

function mapLedgerError(err: unknown): string {
  if (err instanceof AdminReviewApiError) {
    if (err.status === 403 || err.status === 401) {
      return 'Admin key is required to view insertion records.';
    }
    if (err.status === 404) {
      return 'Motion Review Notes insertion records are not available for this report.';
    }
    return 'Unable to load Motion Review Notes insertion records.';
  }
  if (err instanceof Error) {
    return 'Unable to load Motion Review Notes insertion records.';
  }
  return 'Unable to load Motion Review Notes insertion records.';
}

const MotionReviewNotesInsertionLedgerPanel: React.FC<
  MotionReviewNotesInsertionLedgerPanelProps
> = ({ uploadId }) => {
  const [payload, setPayload] = useState<MotionReviewNotesInsertionRecordsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLedger = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchMotionReviewNotesInsertionRecords(uploadId);
      setPayload(response);
    } catch (err: unknown) {
      setPayload(null);
      setError(mapLedgerError(err));
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => {
    if (!uploadId) {
      setPayload(null);
      setError(null);
      return;
    }
    loadLedger();
  }, [uploadId, loadLedger]);

  if (!uploadId) {
    return null;
  }

  return (
    <MotionReviewNotesInsertionLedgerPanelView
      uploadId={uploadId}
      payload={payload}
      loading={loading}
      error={error}
      onRefresh={loadLedger}
    />
  );
};

export default MotionReviewNotesInsertionLedgerPanel;
